/**
 * Drawer — right-side slide-in panel.
 *
 * Used for forms that need more vertical room than a Modal but don't warrant
 * a full page. The Entity Drilldown's inspection-event editor is the
 * archetypal use: tall form fields (date, outcome, location, notes, resolved
 * date), context-sensitive on which stage the user clicked.
 *
 * Behaviour mirrors Modal: Esc closes, backdrop click closes (unless
 * lockBackdrop), scroll-locks body, focus traps. Width is fixed at 480px
 * per UX spec (UX §10 — drawers always sit at this width on desktop).
 */

import { createPortal } from 'react-dom';
import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  /** Default 480px. The wider variant is reserved for the entity-drilldown
   *  drawer-in-drawer pattern that doesn't ship in v1. */
  width?: 'default' | 'wide';
  lockBackdrop?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

const widthClasses: Record<NonNullable<DrawerProps['width']>, string> = {
  default: 'w-[480px]',
  wide: 'w-[640px]',
};

export function Drawer({
  open,
  onClose,
  title,
  description,
  width = 'default',
  lockBackdrop = false,
  children,
  footer,
  className,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    requestAnimationFrame(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(
        'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    });
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end bg-primary/30"
      onClick={lockBackdrop ? undefined : onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'flex h-full flex-col border-l border-border-default bg-surface shadow-[var(--shadow-overlay)]',
          widthClasses[width],
          className,
        )}
      >
        {(title || description) && (
          <div className="flex items-start justify-between gap-4 border-b border-border-default px-5 py-4">
            <div className="min-w-0">
              {title ? <h2 className="text-base font-semibold text-primary">{title}</h2> : null}
              {description ? (
                <p className="mt-0.5 text-sm text-secondary">{description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-secondary"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-border-default bg-base/40 px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
